import React, { useEffect, useMemo, useState } from 'react';
import {
  buildProductDetailBlocks,
  formatCurrency,
  formatItemMeasure,
  groupSizesByModelo,
  type PersonalizationDetails,
  type ProductDetailBlock,
  type SizeEntry,
} from './orcDetailsModalUtils';
import { StatusArquivosIcons, FileStatusIconsLegend } from '../StatusArquivosIcons';
import { ItemTag } from '../../../ui/ItemTag';
import {
  buildItemIdentityTooltip,
  buildOrderHeaderChipTooltip,
  buildPersonalizationTypeTooltip,
} from '../../utils/pedidosTooltips';
import { OrcLayoutsSection } from './OrcLayoutsSection';
import { OrcObservationsSection } from './OrcObservationsSection';
import {
  fetchCrossTenantLinksByOrder,
  type CrossTenantLinkRead,
} from '../../../Tools/QuotationCalculator/services/crossTenantLinkService';

interface OrcDetailsModalProps {
  pedido: any;
  item: any;
  onClose: () => void;
}

/** Rótulos amigáveis dos tenants (espelha o painel admin cross-tenant). */
const CROSS_TENANT_LABELS: Record<string, string> = {
  oneuniformes: 'One Uniformes',
  one1print: 'One Print',
  one1printdf: 'One Print DF',
  dev: 'Dev (testes)',
};
const tenantLabel = (slug: string): string => CROSS_TENANT_LABELS[slug] ?? slug;

const itemHasArquivo = (entry: any): boolean => entry?.arquivo != null;

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const StatusChip: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
  <span
    className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-navy-700/80 text-gray-300 border border-navy-600 whitespace-nowrap"
    title={title}
  >
    {children}
  </span>
);

/** Ícone da marca WhatsApp (SVG inline, self-contained). */
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/**
 * Botão de copiar reutilizável (co-located). Copia `value` para a área de
 * transferência e mostra um feedback breve de "copiado". Só renderiza quando há
 * um valor não-vazio para copiar.
 */
const CopyButton: React.FC<{ value?: string | null; label?: string; className?: string }> = ({
  value,
  label = 'Copiar',
  className = '',
}) => {
  const [copied, setCopied] = useState(false);
  const text = typeof value === 'string' ? value.trim() : '';

  if (!text) return null;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard indisponível (contexto não seguro): silencioso, sem quebrar a UI.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copiado!' : `${label}: ${text}`}
      aria-label={`${label} ${text}`}
      className={`inline-flex items-center justify-center shrink-0 rounded p-0.5 text-gray-400 hover:text-yellow-300 hover:bg-navy-700/60 transition-colors ${className}`}
    >
      {copied ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-emerald-400">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
};

const SizeQuantityChip: React.FC<{ entry: SizeEntry }> = ({ entry }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-cyan-400/35 bg-cyan-500/10 text-xs font-semibold text-cyan-100"
    title={entry.modelo ? `${entry.tamanho} · ${entry.modelo}` : entry.tamanho}
  >
    <span className="text-cyan-200">{entry.tamanho}</span>
    <span className="text-white tabular-nums">{entry.quantidade}</span>
  </span>
);

const PersonalizationSection: React.FC<{ personalization: PersonalizationDetails }> = ({ personalization }) => {
  const { tecido, cor, tipo, tamanhos, modelos, totalQuantidade } = personalization;
  const hasFabricOrColor = Boolean(tecido || cor);
  const sizeGroups = useMemo(() => groupSizesByModelo(tamanhos, modelos), [tamanhos, modelos]);
  const isGroupedByModel = sizeGroups.some((group) => group.modelo.length > 0);

  return (
    <div className="space-y-2 mt-2 pt-2 border-t border-navy-600/60">
      {(hasFabricOrColor || tipo) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {tecido && (
            <span
              className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md border border-navy-600 bg-navy-700/50 text-[10px]"
              title={`Tecido: ${tecido}`}
            >
              <span className="text-gray-400 uppercase tracking-wide shrink-0">Tecido</span>
              <span className="text-gray-100 font-medium truncate">{tecido}</span>
            </span>
          )}
          {cor && (
            <span
              className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md border border-navy-600 bg-navy-700/50 text-[10px]"
              title={`Cor: ${cor}`}
            >
              <span className="text-gray-400 uppercase tracking-wide shrink-0">Cor</span>
              <span className="text-gray-100 font-medium truncate">{cor}</span>
            </span>
          )}
          {tipo && (
            <span
              className="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase bg-navy-700 text-yellow-300 border border-yellow-400/25"
              title={buildPersonalizationTypeTooltip(tipo)}
            >
              {tipo}
            </span>
          )}
        </div>
      )}

      {sizeGroups.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
            {`Tamanhos${totalQuantidade != null ? ` (${totalQuantidade} un)` : ''}`}
          </div>

          {isGroupedByModel ? (
            <div className="space-y-2.5">
              {sizeGroups.map((group) => (
                <div key={group.modelo} className="space-y-1">
                  <div className="text-[10px] font-semibold text-gray-200 uppercase tracking-wide">
                    {group.modelo}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {group.tamanhos.map((entry, index) => (
                      <SizeQuantityChip key={`${group.modelo}-${entry.tamanho}-${index}`} entry={entry} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {sizeGroups[0]?.tamanhos.map((entry, index) => (
                <SizeQuantityChip key={`${entry.tamanho}-${index}`} entry={entry} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProductBlockCard: React.FC<{ block: ProductDetailBlock; showFileStatus: boolean; orcId?: number }> = ({
  block,
  showFileStatus,
  orcId,
}) => {
  const { finalItem, operationalItem, displayName, personalization } = block;
  const materialName = finalItem?.material?.nome;
  const showFinalFileStatus = showFileStatus && itemHasArquivo(finalItem);
  // Evita quantidade repetida: quando há personalização, o total já aparece em
  // "Tamanhos (N un)". Aí a linha de meta mostra só o material (se houver).
  const metaParts = [
    personalization ? null : formatItemMeasure(finalItem),
    materialName || null,
  ].filter(Boolean);
  // Produção ancora na impressão; aqui mostramos qual PRODUTO vendido (camiseta)
  // ela atende, via linked_context. Em vendas o finalItem já é a camiseta, então
  // não repete.
  const finalProductRef =
    finalItem?.is_impressao_vinculada === true
      ? finalItem?.linked_context?.final_product?.pro_nome
      : null;
  const finalProductOit = finalItem?.linked_context?.final_oit_id;

  return (
    <div className="rounded-lg border border-navy-600 bg-navy-900/40 p-3 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-gray-100 leading-snug" title={displayName}>
            {displayName}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {showFinalFileStatus && (
            <StatusArquivosIcons item={finalItem} size={14} className="flex-shrink-0" />
          )}
          <span
            className="text-yellow-300 font-semibold text-xs"
            title={buildItemIdentityTooltip(orcId, finalItem?.oit_id)}
          >
            {`I-${finalItem?.oit_id ?? '—'}`}
          </span>
        </div>
      </div>

      {metaParts.length > 0 && (
        <div className="text-[11px] text-gray-400">{metaParts.join(' · ')}</div>
      )}

      {finalProductRef && (
        <div className="text-[11px] text-gray-300">
          <span className="text-gray-500">Produto: </span>
          <span className="font-medium" title={finalProductRef}>{finalProductRef}</span>
          {finalProductOit != null && (
            <span className="text-gray-500"> · I-{finalProductOit}</span>
          )}
        </div>
      )}

      {personalization && <PersonalizationSection personalization={personalization} />}

      {operationalItem && (
        <div className="mt-2 pt-2 border-t border-navy-700/80">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Produção interna
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-gray-400 min-w-0">
              <span
                className="text-yellow-300/80 font-medium"
                title={buildItemIdentityTooltip(orcId, operationalItem.oit_id)}
              >
                {`I-${operationalItem.oit_id}`}
              </span>
              {' · '}
              <span>{operationalItem?.produto?.nome || '—'}</span>
              {' · '}
              <span>{formatItemMeasure(operationalItem)}</span>
            </div>
            {showFileStatus && itemHasArquivo(operationalItem) && (
              <StatusArquivosIcons item={operationalItem} size={14} className="flex-shrink-0" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// A metragem da impressão só é conhecida após a produção — o vendedor salva a
// arte sem saber o consumo de papel. Enquanto for 0/nula, mostra "a calcular"
// em vez de um "0 m" enganoso.
const formatImpressionMeasure = (item: any): string => {
  const q = Number(item?.quantidade);
  return Number.isFinite(q) && q > 0 ? formatItemMeasure(item) : 'a calcular';
};

/**
 * Linha da lista técnica no sentido correto do vínculo: o item COMERCIAL (ex.:
 * camiseta) é o PAI e a IMPRESSÃO operacional aparece recuada como FILHO — a
 * venda é da camiseta; a impressão é derivada dela. Casos cobertos:
 *  - camiseta + impressão pareada → camiseta (pai) ↳ impressão (filho)
 *  - impressão órfã (comercial fora do lote) → reconstrói o pai via linked_context
 *  - produto sem impressão → só a linha do produto
 * A hierarquia é dirigida pela flag `is_impressao_vinculada` (não por categoria).
 */
const TechnicalBlockRow: React.FC<{
  block: ProductDetailBlock;
  showFileStatus: boolean;
}> = ({ block, showFileStatus }) => {
  const { finalItem, operationalItem } = block;
  const isOrphanImpression =
    !operationalItem && finalItem?.is_impressao_vinculada === true;

  const parent = isOrphanImpression
    ? {
        oitId: finalItem?.linked_context?.final_oit_id,
        nome: finalItem?.linked_context?.final_product?.pro_nome,
        item: null as any,
      }
    : { oitId: finalItem?.oit_id, nome: finalItem?.produto?.nome, item: finalItem };
  const child = isOrphanImpression ? finalItem : operationalItem;

  return (
    <div className="rounded-md border border-navy-700/60 bg-navy-800/40 p-2">
      {/* Pai: item comercial (o que se vende) */}
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {parent.oitId != null ? (
            <ItemTag itemId={parent.oitId} size="xs" />
          ) : (
            <span className="text-gray-500 text-[11px] font-mono">I-—</span>
          )}
          <span className="text-gray-200 text-[11px] truncate" title={parent.nome || '—'}>
            {parent.nome || '—'}
          </span>
        </div>
        {parent.item && (
          <div className="flex items-center gap-1.5 shrink-0">
            {showFileStatus && itemHasArquivo(parent.item) && (
              <StatusArquivosIcons item={parent.item} size={12} className="flex-shrink-0" />
            )}
            <span className="text-gray-400 text-[10px]">{formatItemMeasure(parent.item)}</span>
          </div>
        )}
      </div>

      {/* Filho: impressão operacional (derivada da camiseta) */}
      {child && (
        <div className="flex items-start justify-between gap-2 pl-4 mt-1.5 pt-1.5 border-t border-navy-700/40 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-gray-500 text-[11px]">↳</span>
            {child?.oit_id != null && <ItemTag itemId={child.oit_id} size="xs" />}
            <span className="text-gray-400 text-[11px] truncate" title={child?.produto?.nome || '—'}>
              {child?.produto?.nome || '—'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {showFileStatus && itemHasArquivo(child) && (
              <StatusArquivosIcons item={child} size={12} className="flex-shrink-0" />
            )}
            <span className="text-gray-500 text-[10px]">{formatImpressionMeasure(child)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Seção de vínculo cross-tenant (SPEC-15 reload): reexibe, ao reabrir o pedido,
 * os vínculos origem→destino persistidos. Só aparece quando o pedido tem vínculo.
 */
const CrossTenantLinkSection: React.FC<{ links: CrossTenantLinkRead[] }> = ({ links }) => {
  if (links.length === 0) return null;
  return (
    <div className="space-y-2 pt-1 border-t border-navy-700">
      <div className="text-[11px] font-semibold text-teal-300 uppercase tracking-wide">
        Vínculo cross-tenant
      </div>
      {links.map((link) => {
        // Retrocompat 1:1: quando não há contribuintes na tabela filha, usa as
        // colunas source_* primárias do próprio vínculo.
        const effectiveSources =
          link.sources.length > 0
            ? link.sources
            : link.sourceOrcId != null && link.sourceOitId != null
              ? [
                  {
                    sourceTenantId: link.sourceTenantId,
                    sourceClientId: link.sourceClientId,
                    sourceOrcId: link.sourceOrcId,
                    sourceOitId: link.sourceOitId,
                    sourceRole: null,
                  },
                ]
              : [];
        return (
          <div
            key={link.id}
            className="rounded-lg border border-teal-500/25 bg-teal-500/5 p-2.5 space-y-1.5"
          >
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-gray-400">Origem</span>
              <span className="text-gray-100 font-medium">{tenantLabel(link.sourceTenantId)}</span>
              <span className="text-gray-500">→</span>
              <span className="text-gray-100 font-medium">{tenantLabel(link.targetTenantId)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-gray-400">Item no destino:</span>
              <ItemTag itemId={link.targetOitId} size="xs" />
            </div>
            <div className="space-y-1">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                {`Itens de origem (${effectiveSources.length})`}
              </div>
              {effectiveSources.map((s, index) => (
                <div
                  key={`${s.sourceOrcId}-${s.sourceOitId}-${index}`}
                  className="flex items-center gap-1.5 pl-1 text-[11px] min-w-0"
                >
                  <span className="text-gray-500">↳</span>
                  <span className="text-gray-400 font-mono">{`O-${s.sourceOrcId}`}</span>
                  <ItemTag itemId={s.sourceOitId} size="xs" />
                  {s.sourceRole && <span className="text-gray-500 truncate">({s.sourceRole})</span>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const OrcDetailsModal: React.FC<OrcDetailsModalProps> = ({ pedido, onClose }) => {
  const [showTechnicalList, setShowTechnicalList] = useState(false);
  // Bloco de itens colapsado por padrão: a modal abre do fluxo Corel onde o item
  // já está em edição, então o foco é o contexto do pedido (cliente/valores).
  const [showItens, setShowItens] = useState(false);
  const [crossLinks, setCrossLinks] = useState<CrossTenantLinkRead[]>([]);

  // Reidrata os vínculos cross-tenant do pedido ao abrir (best-effort: falha
  // não quebra o modal; pedidos sem vínculo simplesmente não exibem a seção).
  useEffect(() => {
    const orcId = Number(pedido?.orc_id);
    if (!Number.isInteger(orcId) || orcId <= 0) {
      setCrossLinks([]);
      return;
    }
    let cancelled = false;
    fetchCrossTenantLinksByOrder(orcId)
      .then((links) => {
        if (!cancelled) setCrossLinks(links);
      })
      .catch(() => {
        if (!cancelled) setCrossLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pedido?.orc_id]);

  const cliente = pedido.cliente?.nome_fantasia || pedido.cliente?.nome || '—';
  const clienteWhatsapp: string | null =
    typeof pedido.cliente?.whatsapp === 'string' && pedido.cliente.whatsapp.trim().length > 0
      ? pedido.cliente.whatsapp.trim()
      : null;
  const data = pedido.data_cadastro || pedido.data_emissao;
  const originalItems = Array.isArray((pedido as any)?.__originalItems)
    ? (pedido as any).__originalItems
    : pedido?.itens || [];

  const productBlocks = useMemo(() => buildProductDetailBlocks(originalItems), [originalItems]);

  // Trata total 0/nulo como "sem valor" (ex.: orçamento ainda não fechado) — só
  // exibe quando há um total positivo. Frete/desconto já filtram por > 0.
  const valorTotalNumber = Number(pedido?.valor_total);
  const valorTotal =
    Number.isFinite(valorTotalNumber) && valorTotalNumber > 0
      ? formatCurrency(pedido?.valor_total)
      : null;
  const valorFrete = Number(pedido?.valor_frete) > 0 ? formatCurrency(pedido?.valor_frete) : null;
  const valorDesconto = Number(pedido?.valor_desconto) > 0 ? formatCurrency(pedido?.valor_desconto) : null;
  // Esconde a seção inteira (inclusive o título) quando não há nenhum valor.
  const showValores = Boolean(valorTotal || valorFrete || valorDesconto);
  const showTechnicalToggle = originalItems.length > 1;
  const showFileStatus = (pedido as any)?.__role === 'producao';
  const formattedHeaderDate = formatDate(data);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-navy-800 border border-navy-600 rounded-xl shadow-xl w-[92vw] max-w-md max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-4 pb-3 border-b border-navy-600">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                Detalhes do pedido
              </span>
              <div className="text-base font-bold text-white truncate mt-0.5" title={`O-${pedido.orc_id} · ${cliente}`}>
                {`O-${pedido.orc_id} · ${cliente}`}
              </div>
              {/* Nome fantasia já aparece no título (O-XX · CLIENTE); aqui só o
                  contato de WhatsApp — compacto e mobile-first. */}
              {clienteWhatsapp && (
                <div className="flex items-center gap-1.5 mt-1 min-w-0">
                  <WhatsAppIcon className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="text-[11px] text-gray-200 truncate" title={clienteWhatsapp}>{clienteWhatsapp}</span>
                  <CopyButton value={clienteWhatsapp} label="Copiar WhatsApp" />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {pedido?.status?.descricao_pagamento && (
                  <StatusChip title={buildOrderHeaderChipTooltip('pagamento', pedido.status.descricao_pagamento)}>
                    {pedido.status.descricao_pagamento}
                  </StatusChip>
                )}
                {pedido?.status?.descricao && (
                  <StatusChip title={buildOrderHeaderChipTooltip('status', pedido.status.descricao)}>
                    {pedido.status.descricao}
                  </StatusChip>
                )}
                <StatusChip title={buildOrderHeaderChipTooltip('data', formattedHeaderDate)}>
                  {formattedHeaderDate}
                </StatusChip>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-100 transition-colors text-lg leading-none shrink-0 p-1"
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4 text-xs overflow-y-auto max-h-[calc(85vh-120px)]">
          <OrcObservationsSection
            orderObservation={pedido?.observacao}
            items={originalItems}
          />

          <OrcLayoutsSection orcId={Number(pedido.orc_id)} />

          <div className="space-y-2">
            <div className="flex items-end justify-between gap-2 border-b border-navy-600 pb-1">
              <button
                type="button"
                onClick={() => setShowItens((open) => !open)}
                className="flex items-center gap-1.5 text-[11px] font-semibold text-yellow-300 uppercase tracking-wide hover:text-yellow-200 transition-colors"
                aria-expanded={showItens}
              >
                <span className="text-gray-500 text-[10px]">{showItens ? '▲' : '▼'}</span>
                {`Itens (${productBlocks.length})`}
              </button>
              {showFileStatus && showItens && (
                <FileStatusIconsLegend className="justify-end" />
              )}
            </div>
            {showItens && (
              <div className="space-y-2">
                {productBlocks.map((block) => (
                  <ProductBlockCard
                    key={block.finalItem?.oit_id ?? block.displayName}
                    block={block}
                    showFileStatus={showFileStatus}
                    orcId={pedido.orc_id}
                  />
                ))}
              </div>
            )}
          </div>

          <CrossTenantLinkSection links={crossLinks} />

          {showTechnicalToggle && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowTechnicalList((open) => !open)}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border border-navy-600 bg-navy-900/30 text-[11px] font-medium text-gray-300 hover:bg-navy-900/50 transition-colors"
              >
                <span>{showTechnicalList ? 'Ocultar lista técnica' : `Ver todos os itens do pedido (${originalItems.length})`}</span>
                <span className="text-gray-500">{showTechnicalList ? '▲' : '▼'}</span>
              </button>
              {showTechnicalList && (
                <div className="space-y-1.5">
                  {/* Insumo (is_consumo_base, ex.: tecido) fica fora da lista técnica. */}
                  {productBlocks
                    .filter((block) => block.finalItem?.is_consumo_base !== true)
                    .map((block) => (
                      <TechnicalBlockRow
                        key={block.finalItem?.oit_id ?? block.displayName}
                        block={block}
                        showFileStatus={showFileStatus}
                      />
                    ))}
                </div>
              )}
            </div>
          )}

          {showValores && (
            <div className="space-y-2 pt-1 border-t border-navy-700">
              <div className="text-[11px] font-semibold text-yellow-300 uppercase tracking-wide">
                Valores
              </div>
              <div className="space-y-1.5 text-[11px]">
                {valorTotal && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-400">Total</span>
                    <span className="text-gray-100 font-medium">{valorTotal}</span>
                  </div>
                )}
                {valorFrete && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-400">Frete</span>
                    <span className="text-gray-100 font-medium">{valorFrete}</span>
                  </div>
                )}
                {valorDesconto && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-400">Desconto</span>
                    <span className="text-gray-100 font-medium">{valorDesconto}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
